import cn from "classnames";
import { AddressField, Field, FileField, Select } from "components/shared/form";
import { requiredField } from "helpers/validators";
import React, { useState } from "react";
import { editInfoToCreateDocument, getBanks } from "sources/profile/organization";

const allowedImageMimeTypes = ["image/png", "image/jpeg", "image/webp"];

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        if (!file) {
            resolve(null);
            return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const { result } = reader;
            if (typeof result !== "string") {
                reject(new Error("Failed to read file as DataURL: unexpected result type"));
                return;
            }
            const parts = result.split(",");
            if (parts.length < 2) {
                reject(new Error("Failed to read file as DataURL: invalid format"));
                return;
            }
            const base64String = parts[1];
            resolve(base64String);
        };
        reader.onerror = (error) => {
            reject(error);
        };
    });
}

function getImageMimeType(base64String) {
    if (base64String.startsWith("/9j/")) {
        return "image/jpeg";
    }
    if (base64String.startsWith("UklGR")) {
        return "image/webp";
    }
    return "image/png";
}

function getBase64ImageSrc(value) {
    if (!value || typeof value !== "string") {
        return null;
    }
    const base64String = value.trim();
    const mimeTypeMatch = base64String.match(/^data:(image\/[a-z]+);base64,/i);
    if (mimeTypeMatch) {
        if (!allowedImageMimeTypes.includes(mimeTypeMatch[1].toLowerCase())) {
            return null;
        }
        return base64String;
    }

    return `data:${getImageMimeType(base64String)};base64,${base64String}`;
}

export default function OrganizationRequiredInfoModal(props) {
    const { data, onSuccess, back, roles } = props;

    const isAdmin = roles?.isTruckingOrganizationAdmin || roles?.isAdmin;

    const [loading, setLoading] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const [formData, setFormData] = useState({
        id: data?.id,
        address: data?.address || "",
        city: data?.city || null,
        street: data?.street || null,
        houseNumber: data?.houseNumber || null,
        location: data?.location || null,
        bank: data?.bank || "",
        bankId: data?.bankId || "",
        ks: data?.ks || "",
        rs: data?.rs || "",
        management: {
            position: data?.management?.position || "",
            firstName: data?.management?.firstName || "",
            lastName: data?.management?.lastName || "",
            patronymic: data?.management?.patronymic || null,
            stamp: data?.management?.stamp || null,
            signature: data?.management?.signature || null,
        },
        accountant: {
            position: data?.accountant?.position || "",
            firstName: data?.accountant?.firstName || "",
            lastName: data?.accountant?.lastName || "",
            patronymic: data?.accountant?.patronymic || null,
            signature: data?.accountant?.signature || null,
        },
    });

    const [files, setFiles] = useState({
        managementStamp: null,
        managementSignature: null,
        accountantSignature: null,
    });

    const handleAddressChange = ({ value }) => {
        setFormData((prev) => ({
            ...prev,
            address: value.address || "",
            city: value.city || null,
            street: value.street || null,
            houseNumber: value.houseNumber || null,
            location: value.location || null,
        }));
    };

    const handleRequiredInfoChange = ({ name, value }) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleBankChange = ({ value, option }) => {
        setFormData((prev) => {
            if (!option) {
                return {
                    ...prev,
                    bank: "",
                    bankId: value || "",
                    ks: "",
                };
            }

            return {
                ...prev,
                bank: option.bank || "",
                bankId: option.bankId || value || "",
                ks: option.ks || "",
                rs: option.rs || prev.rs,
            };
        });
    };

    const handleManagementChange = ({ name, value }) => {
        setFormData((prev) => ({
            ...prev,
            management: { ...prev.management, [name]: value },
        }));
    };

    const handleAccountantChange = ({ name, value }) => {
        setFormData((prev) => ({
            ...prev,
            accountant: { ...prev.accountant, [name]: value },
        }));
    };

    const handleFileChange = ({ name, value }) => {
        setFiles((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            setInvalid(false);
            let managementStampB64 = formData.management.stamp;
            if (files.managementStamp) {
                managementStampB64 = await fileToBase64(files.managementStamp);
            }

            let managementSignatureB64 = formData.management.signature;
            if (files.managementSignature) {
                managementSignatureB64 = await fileToBase64(files.managementSignature);
            }

            let accountantSignatureB64 = formData.accountant.signature;
            if (files.accountantSignature) {
                accountantSignatureB64 = await fileToBase64(files.accountantSignature);
            }

            const payload = {
                ...formData,
                management: {
                    ...formData.management,
                    stamp: managementStampB64,
                    signature: managementSignatureB64,
                },
                accountant: {
                    ...formData.accountant,
                    signature: accountantSignatureB64,
                },
            };

            const res = await editInfoToCreateDocument(payload);
            if (res) {
                back();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                setInvalid(true);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="card card-body mb-3">
                <p className="text-center text-muted m-0 py-3">
                    Для создания документов необходимо заполнить юридический адрес, а также загрузить печать и подпись
                    организации и банковские реквизиты в настройках профиля. Пожалуйста, обратитесь к администратору.
                </p>
            </div>
        );
    }

    const renderField = (label, name, value, onChange, required = false) => (
        <Field
            labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
            fieldWrapper="col-12"
            label={label}
            inputProps={{ name }}
            value={value}
            onChange={onChange}
            required={required}
            validators={required ? [requiredField] : []}
        />
    );

    const renderBankIdField = () => (
        <Select
            labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
            fieldWrapper="col-12"
            label="БИК"
            inputProps={{ name: "bankId" }}
            value={formData.bankId}
            onChange={handleBankChange}
            required
            validators={[requiredField]}
            url={getBanks}
            propName={(item) => [item.bankId, item.bank].filter(Boolean).join(" - ")}
            idKey="bankId"
            minimumInputLength={3}
            menuPortalTarget={document.body}
        />
    );

    const renderFileField = (label, name, value, onChange, required = false, storedValue = null) => {
        const requiredFile = (value) => (value || storedValue ? "" : requiredField(value));
        const hasStoredImage = !value && !!getBase64ImageSrc(storedValue);
        const fieldValue = hasStoredImage ? { name: "Загружено" } : value;

        return (
            <div className="mb-2">
                <FileField
                    label={label}
                    labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
                    fieldWrapper="col-12"
                    inputProps={{
                        name,
                        className: hasStoredImage ? "bg-light text-muted" : undefined,
                        readOnly: hasStoredImage,
                    }}
                    value={fieldValue}
                    onChange={onChange}
                    accept="image/png,image/webp,image/jpeg"
                    required={required}
                    validators={required ? [requiredFile] : []}
                />
            </div>
        );
    };

    const renderUploadedImage = (label, value) => {
        const src = getBase64ImageSrc(value);

        if (!src) {
            return null;
        }

        return (
            <div className="col-12 mb-2">
                <img
                    src={src}
                    alt={label}
                    className="img-thumbnail d-block"
                    style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain" }}
                />
            </div>
        );
    };

    return (
        <div className={cn("mb-3", { "loading loading--small": loading, "modal-window__form--invalid": invalid })}>
            <div className="alert alert-warning mb-3">
                Заполните обязательные данные профиля организации для корректного формирования документов.
            </div>

            <h5>Общая информация</h5>
            <div className="mb-3 row">
                <label className="field-wrap__label--required col-12 col-form-label col-form-label-sm text-muted">
                    Юридический адрес
                </label>
                <div className="col-12">
                    <AddressField
                        inputProps={{ name: "address" }}
                        value={formData.address}
                        geopoint={{ address: formData.address }}
                        onChange={handleAddressChange}
                    />
                </div>
            </div>

            <hr />
            <h5>Банковские реквизиты</h5>
            {renderBankIdField()}
            {renderField("Полное наименование банка", "bank", formData.bank, handleRequiredInfoChange, true)}
            {renderField("Корреспондентский счет", "ks", formData.ks, handleRequiredInfoChange, true)}
            {renderField("Расчетный счет", "rs", formData.rs, handleRequiredInfoChange, true)}

            <hr />
            <h5>Руководство</h5>
            {renderField("Должность", "position", formData.management.position, handleManagementChange, true)}
            {renderField("Фамилия", "lastName", formData.management.lastName, handleManagementChange, true)}
            {renderField("Имя", "firstName", formData.management.firstName, handleManagementChange, true)}
            {renderField("Отчество", "patronymic", formData.management.patronymic, handleManagementChange)}

            <div className="row mt-2 d-flex">
                <div className="col-6">
                    {renderFileField(
                        "Печать",
                        "managementStamp",
                        files.managementStamp,
                        handleFileChange,
                        true,
                        formData.management.stamp,
                    )}
                    {!files.managementStamp && renderUploadedImage("Печать", formData.management.stamp)}
                </div>
                <div className="col-6">
                    {renderFileField(
                        "Подпись",
                        "managementSignature",
                        files.managementSignature,
                        handleFileChange,
                        true,
                        formData.management.signature,
                    )}
                    {!files.managementSignature && renderUploadedImage("Подпись", formData.management.signature)}
                </div>
            </div>

            <hr />
            <h5>Бухгалтер</h5>
            {renderField("Должность", "position", formData.accountant.position, handleAccountantChange)}
            {renderField("Фамилия", "lastName", formData.accountant.lastName, handleAccountantChange)}
            {renderField("Имя", "firstName", formData.accountant.firstName, handleAccountantChange)}
            {renderField("Отчество", "patronymic", formData.accountant.patronymic, handleAccountantChange)}

            <div className="row mt-2 mb-3">
                <div className="col-6">
                    {renderFileField(
                        "Подпись",
                        "accountantSignature",
                        files.accountantSignature,
                        handleFileChange,
                        false,
                        formData.accountant.signature,
                    )}
                    {!files.accountantSignature && renderUploadedImage("Подпись", formData.accountant.signature)}
                </div>
            </div>

            <div className="btn btn-brand btn-block btn-sm" onClick={handleSave}>
                Сохранить и продолжить
            </div>
        </div>
    );
}
